// Pure helpers for the "Order Desk Missed Calls" widget, kept free of React so they
// can be unit tested on their own (same split as receivingTimeHelpers / writtenSalesHelpers).
//
// The API (GET /Dashboard/api/Dashboard/OrderDeskCalls and OrderDeskCallsDetail) serves
// what the RingCentral tie-in pushed: weekly totals as computed there, and every inbound
// call with its outcome. These helpers only reshape, filter and format.
//
// Two kinds of timestamp come back and must not be confused:
//   * startLocal is the Mountain wall-clock time of the call, with no zone. It is
//     formatted from its digits, never through Date, so a viewer in another time zone
//     still sees when the phone actually rang.
//   * lastReceivedUtc is an instant, shown in Denver time.

const DENVER = "America/Denver";
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const LINES = ["Order Desk", "Main greeting", "Other"];
export const ALL_LINES = "All lines";
export const ALL_OUTCOMES = "All outcomes";

// Display order for the outcome filter.
const OUTCOME_ORDER = ["Missed", "Voicemail", "Answered", "After hours", "Quick hang-up", "Hung up during greeting", "Junk"];

const EMPTY_WEEK = {
  weekStart: null,
  weekEnd: null,
  label: "",
  isCurrent: false,
  orderDeskCalls: 0,
  answered: 0,
  missedCalls: 0,
  voicemail: 0,
  missedNoVoicemail: 0,
  quickHangups: 0,
  afterHours: 0,
  junk: 0,
  realCalls: 0,
  answerRate: null,
  greetingHangups: 0,
};

const resultRows = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.result)) return data.result;
  return [];
};

// Map the OrderDeskCalls response (axios `res.data`) to the widget's view model.
// Weeks come oldest first; currentIndex points at the week to open on.
export const normalizeSummary = (data) => {
  const r = resultRows(data)[0];
  if (!r) {
    return { hasData: false, lastReceivedUtc: null, feedProblem: null, weeks: [], currentIndex: -1 };
  }
  const weeks = Array.isArray(r.weeks) ? r.weeks.map((w) => ({ ...EMPTY_WEEK, ...w })) : [];
  let currentIndex = weeks.findIndex((w) => w.isCurrent);
  if (currentIndex < 0) currentIndex = weeks.length - 1;
  return {
    hasData: !!r.hasData && weeks.length > 0,
    lastReceivedUtc: r.lastReceivedUtc || null,
    // Set when the hourly upload hasn't arrived for a while; the figures are then as of the last one.
    feedProblem: r.feedProblem || null,
    weeks,
    currentIndex,
  };
};

export const normalizeCalls = (data) =>
  resultRows(data).map((c) => ({
    ...c,
    isJunk: !!c.isJunk,
    leftVoicemail: !!c.leftVoicemail,
    countsAsMissed: !!c.countsAsMissed,
  }));

// ---------------------------------------------------------------- formatting

// "2026-10-02 09:14:22" or "2026-10-02T09:14:22" -> parts from the digits, no time-zone math.
const wallClock = (v) => {
  if (!v) return null;
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);
  if (!m) return null;
  return { y: +m[1], mo: +m[2], d: +m[3], h: m[4] != null ? +m[4] : null, mi: m[5] != null ? +m[5] : null };
};

const clock = (w) => {
  if (w.h == null) return "";
  const h12 = w.h % 12 === 0 ? 12 : w.h % 12;
  return `${h12}:${String(w.mi).padStart(2, "0")} ${w.h < 12 ? "AM" : "PM"}`;
};

/** "Fri 10/2 9:14 AM" from a Mountain wall-clock value. */
export const fmtCallTime = (v) => {
  const w = wallClock(v);
  if (!w) return "";
  const day = DAYS[new Date(w.y, w.mo - 1, w.d).getDay()];
  return `${day} ${w.mo}/${w.d} ${clock(w)}`.trim();
};

// The API serializes some instants without a trailing Z; they are UTC regardless.
const asInstant = (v) => {
  if (!v) return null;
  const s = String(v);
  const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`);
  return isNaN(d.getTime()) ? null : d;
};

/** A UTC instant shown in Denver time: "Fri 10/9 1:02 PM". */
export const fmtInstant = (v) => {
  const d = asInstant(v);
  if (!d) return "";
  return d
    .toLocaleString("en-US", {
      timeZone: DENVER,
      weekday: "short",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })
    .replace(/,/g, "");
};

/** "+13035551234" -> "(303) 555-1234". Extensions and anything unusual are shown as sent. */
export const fmtPhone = (v) => {
  if (!v) return "";
  const s = String(v);
  const m = s.match(/^\+?1?(\d{3})(\d{3})(\d{4})$/);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : s;
};

/** 0.842 -> "84%". */
export const fmtPercent = (rate) => (rate == null || isNaN(rate) ? "—" : `${Math.round(Number(rate) * 100)}%`);

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** "48 answered · 84% answer rate" */
export const answeredLine = (w) => {
  if (!w || w.orderDeskCalls === 0) return "No Order Desk calls";
  if (w.realCalls === 0) return `${w.answered} answered`;
  return `${w.answered} answered · ${fmtPercent(w.answerRate)} answer rate`;
};

/** "48 answered · 84%" — the short form inside the circle. */
export const answeredShort = (w) => {
  if (!w || w.orderDeskCalls === 0) return "No calls";
  if (w.realCalls === 0) return `${w.answered} answered`;
  return `${w.answered} answered · ${fmtPercent(w.answerRate)}`;
};

/** ["1 junk", "4 quick hang-ups", "7 after hours"] — the calls kept out of the missed count. */
export const screenedParts = (w) =>
  w ? [`${w.junk} junk`, plural(w.quickHangups, "quick hang-up", "quick hang-ups"), `${w.afterHours} after hours`] : [];

/** "Screened out: 1 junk · 4 quick hang-ups · 7 after hours" */
export const screenedLine = (w) => (w ? `Screened out: ${screenedParts(w).join(" · ")}` : "");

/**
 * Missed calls against the week before. More missed calls is worse, so "up" reads red.
 * The current week is still filling in, so it says "so far".
 */
export const changeVsPrevious = (week, previous) => {
  if (!week || !previous) return null;
  const diff = week.missedCalls - previous.missedCalls;
  const dir = diff > 0 ? "up" : diff < 0 ? "down" : "same";
  const arrow = diff > 0 ? "▲" : diff < 0 ? "▼" : "";
  const amount = diff === 0 ? "Same as" : `${arrow} ${Math.abs(diff)} vs`;
  const text = week.isCurrent
    ? `So far: ${amount} last week (${previous.missedCalls})`
    : `${amount} the week before (${previous.missedCalls})`;
  return { dir, text };
};

// Outcome -> pill colour class (red / amber / green / grey).
export const outcomeClass = (outcome) => {
  if (outcome === "Missed") return "odc-out-missed";
  if (outcome === "Voicemail") return "odc-out-voicemail";
  if (outcome === "Answered") return "odc-out-answered";
  return "odc-out-other";
};

// Short tag naming where a caller name or company came from, so users know how far to trust it.
export const SOURCE_TAGS = {
  nancy: "Nancy",
  manual: "known caller",
  transcript: "from voicemail",
  callerId: "caller ID",
};

export const sourceTag = (source, call) => {
  if (source === "transcript" && call && !call.leftVoicemail) return "from call";
  return SOURCE_TAGS[source] || "";
};

/** What the caller wanted: the AI synopsis, else the voicemail transcript. */
export const aboutText = (c) => c.synopsis || c.voicemailTranscript || "";

export const outcomeTitle = (c) => {
  if (c.isJunk && c.junkReason) return `Junk: ${c.junkReason}`;
  if (c.waitSeconds != null && !c.answeredBy) return `Waited ${c.waitSeconds} s before hanging up`;
  if (c.durationSeconds != null) return `Call length ${c.durationSeconds} s`;
  return "";
};

// ---------------------------------------------------------------- filtering

/** Outcomes present on a line, in display order. Junk only when it is being shown. */
export const outcomeOptions = (calls, line, showJunk) => {
  const present = new Set(
    calls.filter((c) => line === ALL_LINES || c.line === line).map((c) => c.outcome)
  );
  return OUTCOME_ORDER.filter((o) => present.has(o) && (showJunk || o !== "Junk")).concat(
    Array.from(present).filter((o) => !OUTCOME_ORDER.includes(o)).sort()
  );
};

const searchable = (c) =>
  [c.callerNumber, fmtPhone(c.callerNumber), c.callerName, c.companyOrProperty, c.answeredBy, aboutText(c), c.routedTo]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

/**
 * The rows the drill-down shows, newest first, plus how many junk calls the junk switch hid
 * (so the count line can say so).
 */
export const filterCalls = (calls, { line = "Order Desk", outcome = ALL_OUTCOMES, showJunk = false, search = "" } = {}) => {
  const onLine = calls.filter((c) => line === ALL_LINES || c.line === line);
  const q = search.trim().toLowerCase();
  const qDigits = q.replace(/\D/g, "");
  const matches = (c) => {
    if (outcome !== ALL_OUTCOMES && c.outcome !== outcome) return false;
    if (!q) return true;
    const text = searchable(c);
    return text.includes(q) || (qDigits.length >= 3 && String(c.callerNumber || "").replace(/\D/g, "").includes(qDigits));
  };
  const rows = onLine.filter((c) => (showJunk || !c.isJunk) && matches(c));
  const hiddenJunk = showJunk ? 0 : onLine.filter((c) => c.isJunk && matches(c)).length;
  rows.sort((a, b) => String(b.startLocal).localeCompare(String(a.startLocal)));
  return { rows, total: onLine.length, hiddenJunk };
};

// ------------------------------------------------------------ Excel export

export const EXPORT_COLUMNS = [
  { key: "when", label: "Date / Time (Mountain)" },
  { key: "line", label: "Line" },
  { key: "phone", label: "Caller Number" },
  { key: "callerName", label: "Caller Name" },
  { key: "callerNameFrom", label: "Name From" },
  { key: "companyOrProperty", label: "Company / Property" },
  { key: "companyFrom", label: "Company From" },
  { key: "outcome", label: "Outcome" },
  { key: "answeredBy", label: "Answered By" },
  { key: "voicemail", label: "Left Voicemail" },
  { key: "about", label: "What They Called About" },
  { key: "junkReason", label: "Junk Reason" },
  { key: "waitSeconds", label: "Wait (s)", type: "number" },
  { key: "durationSeconds", label: "Length (s)", type: "number" },
  { key: "routedTo", label: "Routed To" },
];

export const toExportRows = (calls) =>
  calls.map((c) => ({
    ...c,
    when: fmtCallTime(c.startLocal),
    phone: fmtPhone(c.callerNumber),
    callerNameFrom: sourceTag(c.callerNameSource, c),
    companyFrom: sourceTag(c.companySource, c),
    voicemail: c.leftVoicemail ? "Yes" : "No",
    about: aboutText(c),
  }));
